import { useState, useEffect, useCallback } from 'react';
import { getBounties } from '../services/api';

/**
 * Transform raw bounty data from API into the format expected by the UI
 * Shakespeare.diy API returns bounties with contributors already included
 */
const transformBountyData = (rawData) => {
  if (!rawData || !Array.isArray(rawData)) {
    return [];
  }

  // The Shakespeare API already includes contributors and totalSats
  return rawData.map((row) => ({
    id: row.id,
    meetup: row.meetup_id,
    businessType: row.business_type,
    description: row.description,
    status: row.status || 'active',
    createdAt: row.created_at || new Date().toISOString().split('T')[0],
    claimedAt: row.claimed_at || null,
    verifiedAt: row.verified_at || null,
    completedAt: row.completed_at || null,

    // Contributors: Use existing array, or construct from single contributor fields (legacy/mock support)
    contributors: row.contributors || (row.contributor_name ? [{
      name: row.contributor_name,
      email: row.contributor_email,
      sats: row.sats || 0
    }] : []),

    // Total Sats: Use existing field, or map from 'sats' or sum contributors
    totalSats: row.totalSats || row.sats || 0,

    watchers: row.watchers || [],
    comments: row.comments || [],

    // Claim fields (if bounty is claimed)
    claimedBy: row.claimed_by || null,
    claimerEmail: row.claimer_email || null,
    businessName: row.business_name || null,
    googleMapsLink: row.google_maps_link || null,
    btcMapLink: row.btc_map_link || null,
    verifications: row.verifications || [],
  }));
};

/**
 * Custom hook to fetch and manage bounties
 */
export const useBounties = (meetupId) => {
  const [bounties, setBounties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Mock data fallback when API fails
  const getMockBounties = (meetupId) => {
    const mockData = [
      {
        id: 'mock-taipei-1',
        meetup_id: 'taipei',
        business_type: '☕ Coffee Shop',
        description: 'Any specialty coffee shop in Da\'an District',
        contributor_name: 'Murph',
        contributor_email: 'murph@example.com',
        sats: 100000,
        status: 'active',
        comments: [
          { name: 'Satoshi', text: 'Great idea! There\'s a nice place on Section 2.', date: '2 days ago' }
        ]
      },
      {
        id: 'mock-taipei-2',
        meetup_id: 'taipei',
        business_type: '☕ Coffee Shop',
        description: 'Any specialty coffee shop in Da\'an District',
        contributor_name: 'BitcoinTW',
        contributor_email: 'tw@example.com',
        sats: 100000,
        status: 'active',
        comments: []
      },
      {
        id: 'mock-taipei-3',
        meetup_id: 'taipei',
        business_type: '💈 Barber Shop',
        description: 'Men\'s barber in Xinyi or Zhongshan',
        contributor_name: 'HODL_TW',
        contributor_email: 'hodl@example.com',
        sats: 150000,
        status: 'active',
        comments: [
          { name: 'LightningLiza', text: 'I know a guy who might be interested!', date: '1 day ago' },
          { name: 'Stacker21', text: 'Adding 10k sats to this one.', date: '5 hours ago' }
        ]
      }
    ];

    return mockData.filter(b => b.meetup_id === meetupId);
  };

  const fetchBounties = useCallback(async () => {
    setLoading(true);
    setError(null);

    const result = await getBounties(meetupId);

    if (result.success) {
      const transformed = transformBountyData(result.data);
      setBounties(transformed);
    } else {
      // Use mock data as fallback
      const mockData = getMockBounties(meetupId);
      const transformed = transformBountyData(mockData);
      setBounties(transformed);
      // Don't show error to users, just silently use mock data
      // Developers can check network tab to see API failures
    }

    setLoading(false);
  }, [meetupId]);

  useEffect(() => {
    if (meetupId) {
      fetchBounties();
    }
  }, [meetupId, fetchBounties]);

  return { bounties, loading, error, refetch: fetchBounties };
};

export default useBounties;
